import { useCallback, useEffect, useMemo, useState } from "react";
import type {
  DataTablePaginationProps,
  SortDirection,
} from "./tableTypes";

interface UseSortedPaginatedListOptions<T, SortColumn extends string> {
  items: T[];
  compare: (a: T, b: T, column: SortColumn) => number;
  defaultSortColumn: SortColumn;
  defaultSortDirection?: SortDirection;
  paginate?: boolean;
  /** Page size controlled by the caller (e.g. table preferences). */
  pageSize: number;
}

export function useSortedPaginatedList<T, SortColumn extends string>({
  items,
  compare,
  defaultSortColumn,
  defaultSortDirection = "asc",
  paginate = true,
  pageSize,
}: UseSortedPaginatedListOptions<T, SortColumn>) {
  const [sortColumn, setSortColumn] = useState<SortColumn>(defaultSortColumn);
  const [sortDirection, setSortDirection] =
    useState<SortDirection>(defaultSortDirection);
  const [currentPage, setCurrentPage] = useState(1);

  useEffect(() => {
    setSortColumn(defaultSortColumn);
    setSortDirection(defaultSortDirection);
    setCurrentPage(1);
  }, [defaultSortColumn, defaultSortDirection]);

  useEffect(() => {
    setCurrentPage(1);
  }, [pageSize]);

  const handleSort = (column: SortColumn) => {
    if (sortColumn === column) {
      setSortDirection((direction) => (direction === "asc" ? "desc" : "asc"));
    } else {
      setSortColumn(column);
      setSortDirection("asc");
    }
    setCurrentPage(1);
  };

  const resetPage = useCallback(() => setCurrentPage(1), []);

  const sorted = useMemo(() => {
    const multiplier = sortDirection === "asc" ? 1 : -1;
    return [...items].sort(
      (a, b) => compare(a, b, sortColumn) * multiplier,
    );
  }, [compare, items, sortColumn, sortDirection]);

  const totalPages = Math.max(1, Math.ceil(sorted.length / pageSize));
  const safePage = Math.min(currentPage, totalPages);

  useEffect(() => {
    if (safePage !== currentPage) {
      setCurrentPage(safePage);
    }
  }, [currentPage, safePage]);

  const visibleItems = useMemo(() => {
    if (!paginate) {
      return sorted;
    }
    const start = (safePage - 1) * pageSize;
    return sorted.slice(start, start + pageSize);
  }, [paginate, sorted, safePage, pageSize]);

  const paginationStart =
    sorted.length === 0 ? 0 : (safePage - 1) * pageSize + 1;
  const paginationEnd = Math.min(safePage * pageSize, sorted.length);

  const paginationProps: DataTablePaginationProps = {
    start: paginationStart,
    end: paginationEnd,
    total: sorted.length,
    currentPage: safePage,
    totalPages,
    onPageChange: (page) =>
      setCurrentPage(Math.min(totalPages, Math.max(1, page))),
    isLoading: false,
  };

  return {
    sortColumn,
    sortDirection,
    handleSort,
    resetPage,
    visibleItems,
    showPagination: paginate && sorted.length > 0,
    paginationProps,
  };
}
